import React, {useState} from 'react';
import {connect, DispatchProp} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import {ConfigAddressApi, ConfigApi, StoreConfigCoordinatesApi} from "../../../services/Api/types";
import {RootState} from "../../../../reducers";
import LoadingContainer from "../../../components/Common/LoadingContainer";
import LocationForm from "../../../components/Location/LocationForm";
import {useSessionService} from "../../../services/Session/sessionService";
import {getSession} from "../../../services/Session/reducer";
import {getStoreDefaultConfig} from "../../../services/Store/reducer";
import {ReactComponent as MarkerSVG} from "../../../../images/icons/marker.svg";
import {ReactComponent as LocationSVG} from "../../../../images/icons/location.svg";
import IframeWidget from "../../../utils/IframeWidget";
import PopupModal from "../../../components/Modal/PopupModal";
import {useGeolocation} from "../../../hooks/useGeolocation";
import StringHelper from "../../../services/Common/StringHelper";

interface OwnProps {
    config: ConfigApi,
    showModal: boolean,
    onHide: (address: ConfigAddressApi, addressErrors?: any[]) => void,
    onChange: (address: ConfigAddressApi, coordinates: StoreConfigCoordinatesApi, addressErrors: any[]) => void;
    withoutGeolocation?: boolean;
    title?: string;
    description?: string;
    icon?: JSX.Element;
    iconClassName?: string;
    showOutOfDeliveryZoneModal: boolean;
    setShowOutOfDeliveryZoneModal: React.Dispatch<React.SetStateAction<boolean>>;
    place?: string;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

function ChangeLocationMultistoreModal(props: Props) {
    const [errors, setErrors] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingLocation, setLoadingLocation] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [address, setAddress] = useState(props.session.address);
    const {t} = useTranslation();
    const sessionService = useSessionService();
    const {handleGeolocationError, getPosition} = useGeolocation();
    const [forceStreet, setForceStreet] = useState(false);
    const [checkLocationLoading, setCheckLocationLoading] = useState(!!address);

    if (!props.default_config) return null;

    const geolocation = async () => {
        try {
            setLoadingLocation(true);
            const position = await getPosition();
            await sessionService.updateCoordinates({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
            });
            props.onChange(props.session.address, {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
            }, errors);
        } catch (err) {
            handleGeolocationError(err as GeolocationPositionError);
        } finally {
            setLoadingLocation(false);
        }
    }

    const save = async () => {
        const outOfDeliveryZone = errors.find(error => error.code === "not_in_delivery_area");
        setLoading(true);
        setErrors([]);

        let addressData = address ? {
            city: address.city,
            street: address.street,
            build_nr: address.build_nr,
            flat_nr: address.flat_nr,
            zip_code: address.zip_code,
            country: address.country ? address.country : props.session?.address?.country
        } : {} as ConfigAddressApi;
        try {
            let data = await sessionService.updateLocation(addressData);
            setLoading(false);
            props.onChange(data?.address, data.coordinates, errors);

            if (outOfDeliveryZone) {
                props.setShowOutOfDeliveryZoneModal(true);
            }

            IframeWidget.closeModal();

        } catch (err: any) {
            setLoading(false);
            setErrors(err.errors)
        }
    }
    const onShow = () => {
        setLoadingData(true);
        setAddress(props.session.address)
        setLoadingData(false);
    }
    const onHide = (addressValue: ConfigAddressApi) => {
        setLoadingData(true);
        props.onHide(addressValue, errors);
    }

    const getDescription = () => {
        if(props.description) return props.description;

        return props.withoutGeolocation ? t("modules.multistore.field.enter_your_address.title") : t("modules.multistore.field.locate_your_address.title");
    }

    return (
        <PopupModal
            bodyContent={
            <>
                {props.withoutGeolocation !== true && (
                    <>
                        <button
                            className={`btn btn-outline-primary btn-lg w-100 btn-flex btn-spinner${loadingLocation ? " loading" : ""}`}
                            onClick={geolocation}>
                            <span className="left spinner-border spinner-border-sm"/>
                            {t("modules.multistore.field.locate_my_address.title")} <LocationSVG/>
                        </button>
                        <div className="horizontal-divider">
                            <span>{t("modules.multistore.field.or_provide_data.title")}</span>
                        </div>
                    </>
                )}
                <div>
                    {(!props.showModal || loadingData) ? (
                        <LoadingContainer/>
                    ) : (
                        <LocationForm config={props.default_config}
                                            onErrors={(addressErrors: any) => setErrors(addressErrors)}
                                            onChange={(newAddress: any) => setAddress(newAddress)} errors={errors}
                                            address={address} forceStreet={forceStreet}
                                            setForceStreet={setForceStreet}
                                            forceCheckLocationAddressAtStart
                                            setCheckLocationLoading={setCheckLocationLoading}
                                            place={props.place}
                        />
                    )}
                </div>
            </>
        }
            title={props.title ?? t("modules.multistore.field.check_delivery_options.title")}
            show={props.showModal}
            onHide={() => onHide(address)}
            onShow={onShow}
            headerIcon={props.icon ?? <MarkerSVG/>}
            description={getDescription()}
            primaryButton={
            <button className={"btn-spinner w-100 btn-primary btn btn-submit" + (loading || checkLocationLoading ? ' loading' : '')}
                    onClick={save}
                    disabled={(forceStreet && !address?.street) || checkLocationLoading || (errors.find(error => error.code === "coordinates_not_exist") && StringHelper.getAddressName(address))}>
                <span className="left spinner-border spinner-border-sm"/>
                {t("common.action.save")}
            </button>
        }
        headerIconClassName={props.iconClassName}
        cancelButton
        />
    );
}


const mapStateToProps = (state: RootState) => ({
    session: getSession(state.session),
    default_config: getStoreDefaultConfig(state.config)
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(ChangeLocationMultistoreModal));